import React, { useState } from 'react';
import { useHistory } from 'react-router';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Spinner } from '@admin/components/spinner';
import { regionURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useRegionsQuery } from '@admin/schema';
import { Button } from '@admin/styled/button';
import { ButtonLink } from '@admin/styled/button_link';
import { Card } from '@admin/styled/card';
import { CardToolbar } from '@admin/styled/card_toolbar';
import { CardToolbarLead } from '@admin/styled/card_toolbar_lead';
import { EmptyState } from '@admin/styled/empty_state';
import { MutedText } from '@admin/styled/muted_text';
import { Page } from '@admin/styled/page';
import { PageHeader } from '@admin/styled/page_header';
import { PageTitle } from '@admin/styled/page_title';
import { Pill } from '@admin/styled/pill';
import { Section } from '@admin/styled/section';
import { SectionActions } from '@admin/styled/section_actions';
import { SectionMeta } from '@admin/styled/section_meta';
import { SectionTitle } from '@admin/styled/section_title';
import { Table } from '@admin/styled/table';
import { TableWrap } from '@admin/styled/table_wrap';

import { UpsertModal } from './upsert_modal';

export const Dashboard: React.FC = () => {
  useTitle('Regions');
  const history = useHistory();
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showInactive, setShowInactive] = useState(false);

  const { data, loading } = useRegionsQuery({ client });

  if (loading && !data) {
    return <Spinner />;
  }

  const allRegions = data?.regions ?? [];
  const activeRegions = allRegions.filter((region) => region.active);
  const inactiveRegions = allRegions.filter((region) => !region.active);
  const regions = showInactive ? allRegions : activeRegions;
  const inactiveCount = inactiveRegions.length;

  return (
    <Page>
      <PageHeader>
        <PageTitle>Regions</PageTitle>
      </PageHeader>

      <Section>
        <Card>
          <CardToolbar>
            <CardToolbarLead>
              <SectionTitle>All regions</SectionTitle>
              <SectionMeta>
                {showInactive
                  ? `${allRegions.length} ${allRegions.length === 1 ? 'region' : 'regions'}`
                  : `${activeRegions.length} active`}
                {!showInactive && inactiveCount > 0 && ` · ${inactiveCount} hidden`}
              </SectionMeta>
            </CardToolbarLead>
            <SectionActions>
              {inactiveCount > 0 && (
                <Button variant="secondary" size="sm" type="button" onClick={() => setShowInactive((value) => !value)}>
                  {showInactive ? 'Hide inactive' : `Show ${inactiveCount} inactive`}
                </Button>
              )}
              <Button variant="primary" size="sm" type="button" onClick={() => setShowCreateModal(true)}>
                <FontAwesomeIcon icon={faPlus} /> Add region
              </Button>
            </SectionActions>
          </CardToolbar>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Timezone</th>
                  <th>Parent</th>
                  <th className="col-status">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {regions.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState>
                        {activeRegions.length === 0 && inactiveCount > 0 && !showInactive
                          ? 'No active regions. Show inactive to view archived regions.'
                          : 'No regions yet.'}
                      </EmptyState>
                    </td>
                  </tr>
                ) : (
                  regions.map((region) => (
                    <tr key={region.id}>
                      <td>{region.id}</td>
                      <td>
                        <ButtonLink to={regionURL({ id: region.id })}>{region.name}</ButtonLink>
                      </td>
                      <td>{region.tz}</td>
                      <td>{region.parent?.name ?? <MutedText>None</MutedText>}</td>
                      <td className="col-status">
                        <Pill tone={region.active ? 'success' : 'muted'}>{region.active ? 'Active' : 'Inactive'}</Pill>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <ButtonLink variant="secondary" size="sm" to={regionURL({ id: region.id })}>
                          View details
                        </ButtonLink>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      </Section>

      {showCreateModal && data && (
        <UpsertModal
          warehouseOptions={data.warehouses}
          onClose={(newRegionID) => {
            if (newRegionID) {
              history.push(regionURL({ id: newRegionID }));
            } else {
              setShowCreateModal(false);
            }
          }}
        />
      )}
    </Page>
  );
};
